import React, { forwardRef } from 'react';
import { css } from '../../customEmotion';
import { makeStyles } from '@material-ui/core/styles';

import AccordionSelectGroup, {
  OptionData,
} from './components/AccordionSelectGroup';
import GreyBlock from './components/GreyBlock';
import Placeholder from './components/Placeholder';
import Select, { SelectProps } from '../Select';
import Typography from '../Typography';
import { useMUITheme } from '../styles/theme';

const useSelectStyle = makeStyles(theme => ({
  select: {
    padding: '5px 8px',
    display: 'inline-flex',
    backgroundColor: theme.palette.common.white,
    height: '30px',
  },
}));

interface AccordionSelectProps extends SelectProps {
  options: {
    mainOption: OptionData;
    subOptions: OptionData[];
  }[];
  value: string[];
  handleChangeValue: (value: string[]) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
}

const AccordionSelect = forwardRef<HTMLDivElement, AccordionSelectProps>(
  (props, ref) => {
    const selectStyle = useSelectStyle();
    const {
      value,
      handleChangeValue,
      options,
      variant = 'outlined',
      placeholder = '',
      hint = '',
      disabled = false,
      ...rest
    } = props;

    const theme = useMUITheme();
    const getRenderTexts = (values: string[]) =>
      values.map(value => {
        const [mainOptionValue, subOptionValue] = `${value}`.split('-');
        const option = options.find(
          o => o.mainOption.value === mainOptionValue
        );
        if (!option) {
          return '';
        }
        if (subOptionValue) {
          const subOption = option.subOptions.find(o => o.value === value);
          return subOption
            ? `${option.mainOption.text} ${subOption.text}`
            : option.mainOption.text;
        }
        return option.mainOption.text;
      });

    return (
      <Select
        {...rest}
        ref={ref}
        value={''}
        variant={variant}
        displayEmpty
        fullWidth
        classes={selectStyle}
        disabled={disabled}
        renderValue={
          value.length === 0
            ? () => <Placeholder text={placeholder} disabled={disabled} />
            : () => (
                <div
                  className={css`
                    display: flex;
                    overflow: hidden;
                    white-space: nowrap;
                  `}
                >
                  {getRenderTexts(value).map(v => (
                    <GreyBlock key={v} text={v} />
                  ))}
                </div>
              )
        }
      >
        {hint && (
          <Typography
            variant="body2"
            className={css`
              color: ${theme.palette.grey['500']};
              margin-bottom: 4px;
            `}
          >
            {hint}
          </Typography>
        )}
        {options.map(option => (
          <AccordionSelectGroup
            key={option.mainOption.value}
            mainOption={option.mainOption}
            subOptions={option.subOptions}
            handleChangeValue={handleChangeValue}
            selectedValue={value}
          />
        ))}
      </Select>
    );
  }
);

AccordionSelect.displayName = 'AccordionSelect';

export default AccordionSelect;
